import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Panel, StatusBadge } from "../../components/ui/dashboard";

type Mode = "add" | "list" | "edit";

interface Props {
  mode: Mode;
}

const emptyForm = {
  transporter_id: "",
  registration_number: "",
  vehicle_type: "truck",
  capacity_kg: "",
  is_available: "1",
};

const AdminVehiclePage: React.FC<Props> = ({ mode }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!apiUrl) return;
    if (mode === "list") {
      fetch(`${apiUrl}/auth/vehicles`)
        .then((r) => r.json())
        .then((d) => setRows(Array.isArray(d) ? d : []))
        .catch(() => setRows([]));
    }
  }, [apiUrl, mode]);

  useEffect(() => {
    if (!apiUrl || mode !== "edit" || !id) return;
    fetch(`${apiUrl}/auth/vehicles/${id}`)
      .then((r) => r.json())
      .then((d) => {
        setForm({
          transporter_id: d?.transporter_id ? String(d.transporter_id) : "",
          registration_number: d?.registration_number || "",
          vehicle_type: d?.vehicle_type || "truck",
          capacity_kg: d?.capacity_kg ? String(d.capacity_kg) : "",
          is_available: String(d?.is_available ?? 1),
        });
      })
      .catch(() => setMessage("Could not load vehicle"));
  }, [apiUrl, mode, id]);

  const save = async () => {
    if (!apiUrl) return;
    setLoading(true);
    setMessage(null);
    try {
      const payload = {
        transporter_id: Number(form.transporter_id),
        registration_number: form.registration_number,
        vehicle_type: form.vehicle_type,
        capacity_kg: Number(form.capacity_kg),
        is_available: Number(form.is_available),
      };
      const endpoint = mode === "edit" && id ? `${apiUrl}/auth/vehicles/${id}` : `${apiUrl}/auth/vehicles`;
      const method = mode === "edit" ? "PUT" : "POST";
      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to save vehicle");
      setMessage(mode === "edit" ? "Vehicle updated" : "Vehicle created");
      if (mode === "add") setForm(emptyForm);
      if (mode === "edit") navigate("/admin/vehicles/list");
    } catch (e: any) {
      setMessage(e?.message || "Failed to save vehicle");
    } finally {
      setLoading(false);
    }
  };

  if (mode === "list") {
    return (
      <div className="p-4 md:p-6">
        <Panel title="Vehicles - List" subtitle="Show all vehicles with edit option">
          <div className="mb-3">
            <Link to="/admin/vehicles/add" className="px-3 py-2 rounded-lg text-sm bg-slate-900 text-white">Add Vehicle</Link>
          </div>
          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.id} className="rounded-xl border border-slate-100 p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{row.registration_number}</p>
                  <p className="text-xs text-slate-500">{row.vehicle_type} · {row.capacity_kg} kg · Transporter #{row.transporter_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge label={Number(row.is_available) === 1 ? "available" : "inactive"} tone={Number(row.is_available) === 1 ? "success" : "warning"} />
                  <Link to={`/admin/vehicles/edit/${row.id}`} className="px-2 py-1 text-xs rounded-md bg-slate-100 text-slate-700">Edit</Link>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <Panel title={`${mode === "edit" ? "Edit" : "Add"} Vehicle`} subtitle="Manage truck/vehicle details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Transporter ID" value={form.transporter_id} onChange={(e) => setForm((p) => ({ ...p, transporter_id: e.target.value }))} />
          <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Registration Number" value={form.registration_number} onChange={(e) => setForm((p) => ({ ...p, registration_number: e.target.value }))} />
          <select className="border rounded-lg px-3 py-2 text-sm" value={form.vehicle_type} onChange={(e) => setForm((p) => ({ ...p, vehicle_type: e.target.value }))}>
            <option value="truck">truck</option>
            <option value="van">van</option>
            <option value="tanker">tanker</option>
            <option value="trailer">trailer</option>
          </select>
          <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Capacity (kg)" value={form.capacity_kg} onChange={(e) => setForm((p) => ({ ...p, capacity_kg: e.target.value }))} />
          <select className="border rounded-lg px-3 py-2 text-sm" value={form.is_available} onChange={(e) => setForm((p) => ({ ...p, is_available: e.target.value }))}>
            <option value="1">Available</option>
            <option value="0">Inactive</option>
          </select>
        </div>
        {message ? <p className="text-xs text-slate-600 mt-3">{message}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <Link to="/admin/vehicles/list" className="px-3 py-2 rounded-lg text-sm bg-slate-100 text-slate-700">Back</Link>
          <button onClick={save} disabled={loading} className="px-3 py-2 rounded-lg text-sm bg-indigo-600 text-white disabled:opacity-60">{loading ? "Saving..." : mode === "edit" ? "Update" : "Create"}</button>
        </div>
      </Panel>
    </div>
  );
};

export default AdminVehiclePage;
